import { z } from "zod";

export const compositeCompositionRowSchema = z.object({
    childId: z.number().int().positive("form.required_field"),
    amount: z.number().positive("form.required_field"),
    isMain: z.boolean(),
});

export const genericCompositeSchema = z.object({
    mode: z.literal("generic"),
    name: z.string().trim().min(1, "form.required_field"),
    externalId: z.string().optional(),
    packagingReferenceCompositeId: z.number().int().positive("form.required_field"),
});

export const specificCompositeSchema = z.object({
    mode: z.literal("specific"),
    name: z.string().trim().min(1, "form.required_field"),
    externalId: z.string().optional(),
    composition: z
        .array(compositeCompositionRowSchema)
        .min(1)
        .refine((rows) => rows.filter((row) => row.isMain).length === 1, {
            message: "packaging_composite.composition.requires_single_main",
        }),
});

export const compositeSchema = z.discriminatedUnion("mode", [genericCompositeSchema, specificCompositeSchema]);

export type CompositeFormData = z.infer<typeof compositeSchema>;
export type SpecificCompositeFormData = z.infer<typeof specificCompositeSchema>;

const compositeEditorRowSchema = z.object({
    id: z.number().optional(),
    childId: z.number(),
    amount: z.number(),
    isMain: z.boolean(),
});

export const compositeEditorFormSchema = z
    .object({
        mode: z.enum(["generic", "specific"]),
        name: z.string().trim().min(1, "form.required_field"),
        externalId: z.string(),
        packagingReferenceCompositeId: z.number().nullable(),
        composition: z.array(compositeEditorRowSchema),
        shares: z.array(
            z.object({
                "@id": z.string(),
                id: z.number(),
                name: z.string(),
            }),
        ),
    })
    .superRefine((data, context) => {
        if (data.mode === "generic") {
            if (data.packagingReferenceCompositeId === null || data.packagingReferenceCompositeId <= 0) {
                context.addIssue({
                    code: "custom",
                    path: ["packagingReferenceCompositeId"],
                    message: "form.required_field",
                });
            }
            return;
        }

        if (data.composition.length === 0) {
            context.addIssue({ code: "custom", path: ["composition"], message: "form.required_field" });
            return;
        }

        data.composition.forEach((row, index) => {
            if (row.childId <= 0) {
                context.addIssue({
                    code: "custom",
                    path: ["composition", index, "childId"],
                    message: "form.required_field",
                });
            }
            if (row.amount <= 0) {
                context.addIssue({
                    code: "custom",
                    path: ["composition", index, "amount"],
                    message: "form.required_field",
                });
            }
        });

        if (data.composition.filter((row) => row.isMain).length !== 1) {
            context.addIssue({
                code: "custom",
                path: ["composition"],
                message: "packaging_composite.composition.requires_single_main",
            });
        }
    });

export type CompositeEditorFormData = z.infer<typeof compositeEditorFormSchema>;
